import type { JourneyPath, Lesson, Module, Task } from '../types/journey';
import { normalizeSections, type LessonSection, type RoadDiagramSpec } from './roadDiagram';
import { clampExamCount, clampPassPercent, isLevelBand, PRACTICE_EXAM_PASS_PERCENT, PRACTICE_EXAM_QUESTIONS, type LevelBand } from './practiceModel';

export type ContentStatus = 'draft' | 'published';
export type LessonKind = 'video' | 'reading' | 'quiz' | 'exercise' | 'assessment';
export type CourseLevel = 'beginner' | 'intermediate' | 'advanced' | 'all-levels';

export interface ContentTask {
  input_type: 'checkbox' | 'text' | 'display';
  description_my?: string;
  description_en?: string;
  is_optional?: boolean;
}

export interface ContentQuizQuestion {
  question_my?: string;
  question_en: string;
  options_my?: string[];
  options_en: string[];
  correct_answer: number;
  explanation_my?: string;
  explanation_en?: string;
}

export interface ContentQuiz {
  title_my?: string;
  title_en: string;
  slug?: string;
  questions: ContentQuizQuestion[];
}

export type LevelBandName = LevelBand;

export interface ContentCard {
  image_url?: string;
  diagram?: RoadDiagramSpec | null;
  caption_en?: string;
  caption_my?: string;
  lines_en: string[];
  lines_my: string[];
  key_en: string;
  key_my: string;
  takeaway_en: string;
  takeaway_my: string;
}

export interface ContentLesson {
  id: string;
  path_id: string;
  module_id: string;
  slug: string;
  title_my: string;
  title_en: string;
  body_my: string;
  body_en: string;
  intro_my: string;
  intro_en: string;
  takeaways_my: string;
  takeaways_en: string;
  skill_my: string;
  skill_en: string;
  lesson_type: LessonKind;
  duration: string;
  sort_order: number;
  status: ContentStatus;
  is_premium: boolean;
  youtube_video_id?: string | null;
  video_url?: string | null;
  tasks: ContentTask[];
  deliverables: ContentTask[];
  quiz?: ContentQuiz | null;
  resources?: { title_my?: string; title_en?: string; url: string; description_my?: string; description_en?: string }[];
  cards?: ContentCard[];
  sections?: LessonSection[];
  practice_topic?: string | null;
}

export interface ContentModule {
  id: string;
  path_id: string;
  slug: string;
  title_my: string;
  title_en: string;
  description_my: string;
  description_en: string;
  sort_order: number;
  status: ContentStatus;
  is_premium: boolean;
  level_band?: LevelBand | null;
  lessons: ContentLesson[];
}

export interface GlossaryTerm {
  term_en: string;
  term_my: string;
}

export interface ContentPath {
  id: string;
  slug: string;
  title_my: string;
  title_en: string;
  summary_my: string;
  summary_en: string;
  body_my: string;
  body_en: string;
  duration: string;
  level: CourseLevel;
  is_premium: boolean;
  launch_ready: boolean;
  primary_language: 'my' | 'en';
  glossary: GlossaryTerm[];
  exam_question_count?: number;
  exam_pass_percent?: number;
  status: ContentStatus;
  sort_order: number;
  cover_image_url?: string | null;
  tags?: string[];
  rating?: number;
  enrolled?: number;
  modules: ContentModule[];
}

export function seedId(n: number): string {
  return `a1000000-0000-4000-8000-${String(n).padStart(12, '0')}`;
}

function asTasks(tasks: ContentTask[] | null | undefined, prefix: string): Task[] {
  return (tasks || []).map((task, index) => ({
    _key: `${prefix}-${index}`,
    inputType: task.input_type || 'checkbox',
    description: (task.description_my || '').trim() || task.description_en || '',
    descriptionMy: task.description_my,
    descriptionEn: task.description_en,
    isOptional: Boolean(task.is_optional),
  }));
}

export function contentPathToJourney(path: ContentPath, publishedOnly = true): JourneyPath {
  const englishFirst = path.primary_language === 'en';
  const prefer = (my: string, en: string) => (englishFirst ? (en || '').trim() || my : (my || '').trim() || en);
  const modules = [...(path.modules || [])]
    .filter((module) => (publishedOnly ? module.status === 'published' : true))
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((module): Module => {
      const lessons = [...(module.lessons || [])]
        .filter((lesson) => (publishedOnly ? lesson.status === 'published' : true))
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((lesson): Lesson => ({
          _id: lesson.id,
          title: prefer(lesson.title_my, lesson.title_en),
          titleMy: lesson.title_my,
          titleEn: lesson.title_en,
          slug: lesson.slug,
          order: lesson.sort_order,
          type: lesson.lesson_type || 'reading',
          duration: lesson.duration,
          yourKeySkill: (lesson.skill_my || '').trim() || lesson.skill_en,
          skillMy: lesson.skill_my,
          skillEn: lesson.skill_en,
          introMy: lesson.intro_my,
          introEn: lesson.intro_en,
          bodyMy: lesson.body_my,
          bodyEn: lesson.body_en,
          takeawaysMy: lesson.takeaways_my,
          takeawaysEn: lesson.takeaways_en,
          measurableDeliverables: asTasks(lesson.deliverables, `${lesson.id}-can`),
          actionableTasks: asTasks(lesson.tasks, `${lesson.id}-task`),
          youtubeVideoId: lesson.youtube_video_id || undefined,
          selfHostedVideoUrl: lesson.video_url || undefined,
          videoType: lesson.youtube_video_id ? 'youtube' : lesson.video_url ? 'selfhosted' : undefined,
          lessonResources: (lesson.resources || []).map((resource) => ({
            title: resource.title_my || resource.title_en || resource.url,
            description: resource.description_my || resource.description_en,
            url: resource.url,
            type: 'external' as const,
          })),
          cards: (lesson.cards || [])
            .filter((card) => (card.lines_en || []).length > 0 || (card.lines_my || []).length > 0 || card.key_en || card.key_my)
            .map((card) => ({
              imageUrl: card.image_url,
              diagram: card.diagram || null,
              captionEn: card.caption_en || '',
              captionMy: card.caption_my || '',
              linesEn: card.lines_en || [],
              linesMy: card.lines_my || [],
              keyEn: card.key_en || '',
              keyMy: card.key_my || '',
              takeawayEn: card.takeaway_en || '',
              takeawayMy: card.takeaway_my || '',
            })),
          sections: (lesson.sections || []).map((section) => ({
            heading_en: section.heading_en || '',
            heading_my: section.heading_my || '',
            body_en: section.body_en || '',
            body_my: section.body_my || '',
            diagram: section.diagram || null,
            image_url: section.image_url || null,
            caption_en: section.caption_en || '',
            caption_my: section.caption_my || '',
          })),
          practiceTopic: lesson.practice_topic || null,
          associatedQuiz: lesson.quiz
            ? {
                _id: `${lesson.id}-quiz`,
                title: lesson.quiz.title_my || lesson.quiz.title_en,
                slug: lesson.quiz.slug || `${lesson.slug}-check`,
                questions: lesson.quiz.questions.map((question) => ({
                  question: (question.question_my || '').trim() || question.question_en,
                  questionMy: question.question_my,
                  questionEn: question.question_en,
                  options: question.options_my && question.options_my.length > 0 ? question.options_my : question.options_en,
                  optionsMy: question.options_my,
                  optionsEn: question.options_en,
                  correctAnswer: question.correct_answer,
                  explanation: (question.explanation_my || '').trim() || question.explanation_en || '',
                  explanationMy: question.explanation_my,
                  explanationEn: question.explanation_en,
                })),
              }
            : undefined,
        }));

      return {
        _id: module.id,
        title: prefer(module.title_my, module.title_en),
        titleMy: module.title_my,
        titleEn: module.title_en,
        description: (module.description_my || '').trim() || module.description_en,
        descriptionMy: module.description_my,
        descriptionEn: module.description_en,
        order: module.sort_order,
        levelBand: isLevelBand(module.level_band) ? module.level_band : null,
        isPremium: module.is_premium,
        targetAudience: 'all',
        lessons,
      };
    });

  return {
    _id: path.id,
    title: prefer(path.title_my, path.title_en),
    titleMy: path.title_my,
    titleEn: path.title_en,
    slug: path.slug,
    shortDescription: prefer(path.summary_my, path.summary_en),
    summaryMy: path.summary_my,
    summaryEn: path.summary_en,
    bodyMy: path.body_my,
    bodyEn: path.body_en,
    duration: path.duration,
    level: path.level,
    isPremium: path.is_premium,
    launchReady: path.launch_ready,
    primaryLanguage: englishFirst ? 'en' : 'my',
    examQuestionCount: clampExamCount(path.exam_question_count ?? PRACTICE_EXAM_QUESTIONS),
    examPassPercent: clampPassPercent(path.exam_pass_percent ?? PRACTICE_EXAM_PASS_PERCENT),
    glossary: (path.glossary || [])
      .filter((term) => (term.term_en || '').trim() || (term.term_my || '').trim())
      .map((term) => ({ termEn: term.term_en || '', termMy: term.term_my || '' })),
    rating: path.rating ?? 4.8,
    enrolled: path.enrolled ?? 0,
    tags: path.tags,
    coverImage: path.cover_image_url ? { url: path.cover_image_url, alt: path.title_en } : undefined,
    targetAudience: 'all',
    modules,
  };
}

export function normalizeDbPath(row: Record<string, unknown>): ContentPath {
  const modulesRaw = (row.learning_modules || row.modules || []) as Record<string, unknown>[];
  const modules: ContentModule[] = modulesRaw.map((module) => {
    const lessonsRaw = (module.learning_lessons || module.lessons || []) as Record<string, unknown>[];
    return {
      id: String(module.id),
      path_id: String(module.path_id || row.id),
      slug: String(module.slug || ''),
      title_my: String(module.title_my || ''),
      title_en: String(module.title_en || ''),
      description_my: String(module.description_my || ''),
      description_en: String(module.description_en || ''),
      sort_order: Number(module.sort_order || 0),
      status: module.status === 'draft' ? 'draft' : 'published',
      is_premium: Boolean(module.is_premium),
      level_band: isLevelBand(module.level_band) ? module.level_band : null,
      lessons: lessonsRaw.map((lesson) => ({
        id: String(lesson.id),
        path_id: String(lesson.path_id || row.id),
        module_id: String(lesson.module_id || module.id),
        slug: String(lesson.slug || ''),
        title_my: String(lesson.title_my || ''),
        title_en: String(lesson.title_en || ''),
        body_my: String(lesson.body_my || ''),
        body_en: String(lesson.body_en || ''),
        intro_my: String(lesson.intro_my || ''),
        intro_en: String(lesson.intro_en || ''),
        takeaways_my: String(lesson.takeaways_my || ''),
        takeaways_en: String(lesson.takeaways_en || ''),
        skill_my: String(lesson.skill_my || ''),
        skill_en: String(lesson.skill_en || ''),
        lesson_type: (lesson.lesson_type as ContentLesson['lesson_type']) || 'reading',
        duration: String(lesson.duration || ''),
        sort_order: Number(lesson.sort_order || 0),
        status: lesson.status === 'draft' ? 'draft' : 'published',
        is_premium: Boolean(lesson.is_premium),
        youtube_video_id: (lesson.youtube_video_id as string | null) || null,
        video_url: (lesson.video_url as string | null) || null,
        tasks: Array.isArray(lesson.tasks) ? (lesson.tasks as ContentTask[]) : [],
        deliverables: Array.isArray(lesson.deliverables) ? (lesson.deliverables as ContentTask[]) : [],
        quiz: (lesson.quiz as ContentQuiz | null) || null,
        resources: Array.isArray(lesson.resources) ? (lesson.resources as ContentLesson['resources']) : [],
        cards: Array.isArray(lesson.cards) ? (lesson.cards as ContentCard[]) : [],
        sections: normalizeSections(lesson.sections),
        practice_topic: lesson.practice_topic ? String(lesson.practice_topic) : null,
      })),
    };
  });

  return {
    id: String(row.id),
    slug: String(row.slug || ''),
    title_my: String(row.title_my || ''),
    title_en: String(row.title_en || ''),
    summary_my: String(row.summary_my || ''),
    summary_en: String(row.summary_en || ''),
    body_my: String(row.body_my || ''),
    body_en: String(row.body_en || ''),
    duration: String(row.duration || ''),
    level: (row.level as CourseLevel) || 'beginner',
    is_premium: Boolean(row.is_premium),
    launch_ready: Boolean(row.launch_ready),
    primary_language: row.primary_language === 'en' ? 'en' : 'my',
    exam_question_count: row.exam_question_count == null ? undefined : clampExamCount(Number(row.exam_question_count)),
    exam_pass_percent: row.exam_pass_percent == null ? undefined : clampPassPercent(Number(row.exam_pass_percent)),
    glossary: Array.isArray(row.glossary)
      ? (row.glossary as { term_en?: string; term_my?: string }[]).map((term) => ({
          term_en: String(term?.term_en || ''),
          term_my: String(term?.term_my || ''),
        }))
      : [],
    status: row.status === 'draft' ? 'draft' : 'published',
    sort_order: Number(row.sort_order || 0),
    cover_image_url: (row.cover_image_url as string | null) || null,
    tags: Array.isArray(row.tags) ? (row.tags as string[]) : [],
    rating: typeof row.rating === 'number' ? row.rating : 4.8,
    enrolled: typeof row.enrolled === 'number' ? row.enrolled : 0,
    modules,
  };
}
